import { z } from "zod";
import { zKeyedRecord, zReadonlyArray } from "../zod/utils.js";

import { DisplayTableColumnOutputType } from "../display-table/outputTypes.js";
import { assertNever } from "../errors.js";
import { getNormalEnum } from "../pseudoEnums.js";
import { typedObjectValues } from "../utils/typedObjects.js";

import {
  HqlAggregationFunction,
  HqlAggregationFunctionLiteral,
} from "./hqlAggregationFunctions.js";

// These types are based off of the types in /data-service/src/main/java/com/hex/sql_gen
// and should be kept in sync.

export const CalciteTypeLiteral = z.enum([
  "VARCHAR",
  "INTEGER",
  "BIGINT",
  "FLOAT",
  "DOUBLE",
  "DECIMAL",
  "BOOLEAN",
  "DATE",
  "TIMESTAMP",
  "TIMESTAMPTZ",
]);

export const CalciteType = getNormalEnum(CalciteTypeLiteral);
export type CalciteType = z.infer<typeof CalciteTypeLiteral>;

export const NUMERIC_CALCITE_TYPES: CalciteType[] = [
  CalciteType.BIGINT,
  CalciteType.FLOAT,
  CalciteType.DOUBLE,
  CalciteType.INTEGER,
  CalciteType.DECIMAL,
];

export const TEMPORAL_CALCITE_TYPES: CalciteType[] = [
  CalciteType.DATE,
  CalciteType.TIMESTAMP,
  CalciteType.TIMESTAMPTZ,
];

export function columnTypeToCalciteType(
  columnType: DisplayTableColumnOutputType,
): CalciteType {
  switch (columnType) {
    case "STRING":
      return "VARCHAR";
    case "NUMBER":
      return "FLOAT";
    case "DATETIME":
      return "TIMESTAMP";
    case "DATETIMETZ":
      return "TIMESTAMPTZ";
    case "TIME":
      // this isn't really correct
      return "TIMESTAMP";
    case "UNKNOWN":
      // this probably isn't correct either
      return "VARCHAR";
    case "BOOLEAN":
    case "DATE":
      return columnType;
    default:
      assertNever(columnType, columnType);
  }
}

export function calciteTypeToColumnType(
  calciteType: CalciteType,
): DisplayTableColumnOutputType {
  switch (calciteType) {
    case "VARCHAR":
      return "STRING";
    case "INTEGER":
    case "DOUBLE":
    case "BIGINT":
    case "FLOAT":
    case "DECIMAL":
      return "NUMBER";
    case "TIMESTAMP":
      return "DATETIME";
    case "TIMESTAMPTZ":
      return "DATETIMETZ";
    case "BOOLEAN":
    case "DATE":
      return calciteType;
    default:
      assertNever(calciteType, calciteType);
  }
}

export const HqlSortOrderLiteral = z.enum(["ASCENDING", "DESCENDING"]);
export const HqlSortOrder = getNormalEnum(HqlSortOrderLiteral);
export type HqlSortOrder = z.infer<typeof HqlSortOrderLiteral>;

export const HqlUnaryOperatorLiteral = z.enum(["MINUS", "LOGICALNOT"]);
export const HqlUnaryOperator = getNormalEnum(HqlUnaryOperatorLiteral);
export type HqlUnaryOperator = z.infer<typeof HqlUnaryOperatorLiteral>;

export const HqlBinaryOperatorLiteral = z.enum([
  "PLUS",
  "MINUS",
  "MULTIPLY",
  "DIVIDE",
  "EXPONENT",
  "MODULO",
  "EQUAL",
  "NOTEQUAL",
  "GREATER",
  "GREATEREQUAL",
  "LESS",
  "LESSEQUAL",
  "LOGICALOR",
  "LOGICALAND",
  "CONCAT",
]);
export const HqlBinaryOperator = getNormalEnum(HqlBinaryOperatorLiteral);
export type HqlBinaryOperator = z.infer<typeof HqlBinaryOperatorLiteral>;

export const HqlTruncUnitLiteral = z.enum([
  "year",
  "quarter",
  "month",
  "week",
  "day",
  "hour",
  "minute",
  "second",
  "dayofweek",
]);
export const HqlTruncUnit = getNormalEnum(HqlTruncUnitLiteral);
export type HqlTruncUnit = z.infer<typeof HqlTruncUnitLiteral>;

const UnaryPredicateFunctions = [
  z.literal("IsNull"),
  z.literal("IsFinite"),
] as const;

const BinaryArgPredicateFunctions = [
  z.literal("Contains"),
  z.literal("StartsWith"),
  z.literal("EndsWith"),
] as const;

export const HqlUnaryPredicateFunctionLiteral = z.union(
  UnaryPredicateFunctions,
);
export const HqlUnaryPredicateFunction = getNormalEnum(
  HqlUnaryPredicateFunctionLiteral,
);
export type HqlUnaryPredicateFunction = z.infer<
  typeof HqlUnaryPredicateFunctionLiteral
>;

export const HqlBinaryPredicateFunctionLiteral = z.union(
  BinaryArgPredicateFunctions,
);
export const HqlBinaryPredicateFunction = getNormalEnum(
  HqlBinaryPredicateFunctionLiteral,
);
export type HqlBinaryPredicateFunction = z.infer<
  typeof HqlBinaryPredicateFunctionLiteral
>;

export const HqlScalarFunctionLiteral = z.union([
  ...UnaryPredicateFunctions,
  ...BinaryArgPredicateFunctions,
  z.literal("Abs"),
  z.literal("Round"),
  z.literal("Ceiling"),
  z.literal("Floor"),
  z.literal("Power"),
  z.literal("Sqrt"),
  z.literal("Exp"),
  z.literal("Lower"),
  z.literal("Upper"),
  z.literal("Concat"),
  z.literal("Length"),
  z.literal("Left"),
  z.literal("Right"),
  z.literal("Substitute"),
  z.literal("SplitPart"),
  z.literal("Coalesce"),
  z.literal("ToText"),
  z.literal("ToNumber"),
  z.literal("ToDatetime"),
  z.literal("ToBoolean"),
  z.literal("Year"),
  z.literal("Quarter"),
  z.literal("Month"),
  z.literal("Day"),
  z.literal("DayOfWeek"),
  z.literal("Hour"),
  z.literal("Minute"),
  z.literal("Second"),
  z.literal("Millisecond"),
  z.literal("TruncYear"),
  z.literal("TruncQuarter"),
  z.literal("TruncMonth"),
  z.literal("TruncWeek"),
  z.literal("TruncWeekMonday"),
  z.literal("TruncDay"),
  z.literal("TruncHour"),
  z.literal("TruncMinute"),
  z.literal("TruncSecond"),
  z.literal("DiffDays"),
  z.literal("DiffHours"),
  z.literal("DiffMinutes"),
  z.literal("DiffSeconds"),
  z.literal("DiffMilliseconds"),
  z.literal("Now"),
  z.literal("Today"),
  z.literal("If"),
  z.literal("Switch"),
  z.literal("IsOneOf"),
  z.literal("DatetimeToEpochMs"),
  z.literal("EpochMsToDatetime"),
]);
export const HqlScalarFunction = getNormalEnum(HqlScalarFunctionLiteral);
export type HqlScalarFunction = z.infer<typeof HqlScalarFunctionLiteral>;

// defined in its own file so the Python kernel type generator can resolve it;
// see the note there. Re-exported so every existing import keeps working.
export {
  HqlAggregationFunction,
  HqlAggregationFunctionLiteral,
} from "./hqlAggregationFunctions.js";

const HqlAggregationFunctionSet = new Set(
  typedObjectValues(HqlAggregationFunction).map((s) => s.toLowerCase()),
);
export const isHqlAggregationFunctionName = (s: string): boolean =>
  HqlAggregationFunctionSet.has(s.toLowerCase());

export const HqlNextOnlyFunctionLiteral = z.enum([
  "TruncWeekMonday",
  "SplitPart",
  "CountDistinct",
]);

export const HqlNextOnlyFunction = getNormalEnum(HqlNextOnlyFunctionLiteral);
export type HqlNextOnlyFunction = z.infer<typeof HqlNextOnlyFunctionLiteral>;

const HqlNextOnlyFunctionSet = new Set(
  typedObjectValues(HqlNextOnlyFunction).map((s) => s.toLowerCase()),
);
export const isHqlNextOnlyFunctionName = (s: string): boolean =>
  HqlNextOnlyFunctionSet.has(s.toLowerCase());

export const HqlFunction = z.union([
  HqlScalarFunctionLiteral,
  HqlAggregationFunctionLiteral,
]);
export type HqlFunction = z.infer<typeof HqlFunction>;

export const HqlBool = z.object({
  type: z.literal("boolean"),
  value: z.boolean(),
});
export type HqlBool = z.infer<typeof HqlBool>;

export const HqlFloat = z.object({
  type: z.literal("float"),
  value: z.number(),
});
export type HqlFloat = z.infer<typeof HqlFloat>;

export const HqlInteger = z.object({
  type: z.literal("integer"),
  value: z.number(),
});
export type HqlInteger = z.infer<typeof HqlInteger>;

export const HqlNull = z.object({
  type: z.literal("null"),
});
export type HqlNull = z.infer<typeof HqlNull>;

export const HqlStr = z.object({
  type: z.literal("str"),
  value: z.string(),
});
export type HqlStr = z.infer<typeof HqlStr>;

export const HqlColumn = z.object({
  type: z.literal("column"),
  name: z.string(),
});
export type HqlColumn = z.infer<typeof HqlColumn>;

export const HqlParameter = z.object({
  type: z.literal("parameterReference"),
  name: z.string(),
});
export type HqlParameter = z.infer<typeof HqlParameter>;

export const HqlScalar = z.union([
  HqlBool,
  HqlFloat,
  HqlInteger,
  HqlNull,
  HqlStr,
]);
export type HqlScalar = z.infer<typeof HqlScalar>;

export type HqlUnaryOp = {
  type: "unaryOp";
  op: HqlUnaryOperator;
  left: HqlNode;
};
export const HqlUnaryOp: z.ZodType<HqlUnaryOp, z.ZodTypeDef, unknown> = z.lazy(
  () =>
    z.object({
      type: z.literal("unaryOp"),
      op: HqlUnaryOperatorLiteral,
      left: HqlNode,
    }),
);

export type HqlBinaryOp = {
  type: "binaryOp";
  op: HqlBinaryOperator;
  left: HqlNode;
  right: HqlNode;
};
export const HqlBinaryOp: z.ZodType<HqlBinaryOp, z.ZodTypeDef, unknown> =
  z.lazy(() =>
    z.object({
      type: z.literal("binaryOp"),
      op: HqlBinaryOperatorLiteral,
      left: HqlNode,
      right: HqlNode,
    }),
  );

export type HqlFunc = {
  type: "function";
  name: string;
  args: HqlNode[];
};
export const HqlFunc: z.ZodType<HqlFunc, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    type: z.literal("function"),
    name: z.string(),
    args: z.array(HqlNode),
  }),
);

export const HqlNode = z.union([
  HqlUnaryOp,
  HqlBinaryOp,
  HqlFunc,
  HqlScalar,
  HqlColumn,
  HqlParameter,
]);
export type HqlNode = z.infer<typeof HqlNode>;

const BaseHqlTransform = z.object({
  type: z.string(),
});

export const HqlAggregation = z.object({
  expr: HqlNode,
  as: z.string(),
});
export type HqlAggregation = z.infer<typeof HqlAggregation>;

export const HqlAggregateTransform = BaseHqlTransform.extend({
  type: z.literal("aggregate"),
  groupby: zReadonlyArray(z.string()).optional(),
  aggregations: zReadonlyArray(HqlAggregation),
});
export type HqlAggregateTransform = z.infer<typeof HqlAggregateTransform>;

export const HqlFilterTransform = BaseHqlTransform.extend({
  type: z.literal("filter"),
  expr: HqlNode,
});
export type HqlFilterTransform = z.infer<typeof HqlFilterTransform>;

export const HqlProjection = z.object({
  column: z.string().optional(),
  expr: HqlNode.optional(),
  as: z.string().optional(),
});
export type HqlProjection = z.infer<typeof HqlProjection>;

export const HqlProjectTransform = BaseHqlTransform.extend({
  type: z.literal("project"),
  append: z.boolean().optional(),
  projections: z.array(HqlProjection),
});
export type HqlProjectTransform = z.infer<typeof HqlProjectTransform>;

export const HqlSortColumn = z.object({
  column: z.string(),
  order: HqlSortOrderLiteral.optional(),
});
export type HqlSortColumn = z.infer<typeof HqlSortColumn>;

export const HqlSortTransform = BaseHqlTransform.extend({
  type: z.literal("sort"),
  columns: zReadonlyArray(HqlSortColumn),
});
export type HqlSortTransform = z.infer<typeof HqlSortTransform>;

export const HqlLimitTransform = BaseHqlTransform.extend({
  type: z.literal("limit"),
  limit: z.number(),
  offset: z.number().optional(),
});
export type HqlLimitTransform = z.infer<typeof HqlLimitTransform>;

export const HqlBinTransform = BaseHqlTransform.extend({
  type: z.literal("bin"),
  column: z.string(),
  maxbins: z.number().optional(),
  base: z.number().optional(),
  minstep: z.number().optional(),
  step: z.number().optional(),
  as_prefix: z.string().optional(),
});
export type HqlBinTransform = z.infer<typeof HqlBinTransform>;

export const HqlTransform = z.union([
  HqlAggregateTransform,
  HqlFilterTransform,
  HqlLimitTransform,
  HqlProjectTransform,
  HqlSortTransform,
  HqlBinTransform,
]);
export type HqlTransform = z.infer<typeof HqlTransform>;

export const HqlQueryParameter = z.object({
  name: z.string(),
  type: CalciteTypeLiteral,
});
export type HqlQueryParameter = z.infer<typeof HqlQueryParameter>;

export const HqlQueryColumn = z.object({
  name: z.string(),
  type: CalciteTypeLiteral,
});
export type HqlQueryColumn = z.infer<typeof HqlQueryColumn>;

export const HqlQueryTable = z.object({
  columns: z.array(HqlQueryColumn),
  /** row-based data corresponding to `columns` */
  values: zReadonlyArray(z.array(z.unknown())).optional(),
});
export type HqlQueryTable = z.infer<typeof HqlQueryTable>;

const HqlTableName = z.string();

export const HqlQuery = z.object({
  tables: zKeyedRecord(HqlTableName, HqlQueryTable),
  from: z.string(),
  transforms: zReadonlyArray(HqlTransform).optional(),
});
export type HqlQuery = z.infer<typeof HqlQuery>;
